import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { CONTACT_EMAIL } from "@/lib/site";

// Customer page for a late order: keep it, or cancel for a full refund.
// Opening the link records nothing (mail scanners open links on their own).
// Only the confirm button records the answer, with a POST.

const CHOICE_COPY = {
  keep: { picked: "You picked: keep my order.", button: "Yes, keep my order" },
  cancel: { picked: "You picked: cancel for a full refund.", button: "Yes, cancel and refund" },
};

const THANKS = {
  keep: { title: "Thanks.", body: "Your order stays in line and ships by December 7, 2026. We'll email tracking." },
  cancel: { title: "Done.", body: "Your order is cancelled and you'll get a full refund to your original payment method." },
};

const primaryBtn =
  "w-full sm:w-auto bg-[#F2F0EB] text-[#0A0A0A] px-8 py-4 font-['Oswald'] text-[12px] uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2";
const secondaryBtn =
  "inline-block border border-[#8A8A8A]/40 text-[#F2F0EB] px-8 py-3.5 font-['Oswald'] text-[11px] uppercase tracking-[0.25em] font-semibold hover:border-[#F2F0EB] transition-colors";
const mailLink = "underline underline-offset-2 text-[#F2F0EB] hover:text-[#8A8A8A] transition-colors";

function Shell({ eyebrow, title, children, testid }) {
  return (
    <div data-testid={testid} className="max-w-md w-full text-center">
      {eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-[#8A8A8A]">{eyebrow}</p>}
      <h1 className="mt-5 text-2xl sm:text-3xl font-medium uppercase tracking-[0.14em] leading-snug text-[#F2F0EB]">{title}</h1>
      {children}
    </div>
  );
}

function Email() {
  return <a href={`mailto:${CONTACT_EMAIL}`} className={mailLink}>{CONTACT_EMAIL}</a>;
}

export default function LateOrderPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  // loading | ready | submitting | done | invalid | expired | error
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState({});

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("invalid");
      return;
    }
    api
      .get("/late-order/check", { params: { t: token } })
      .then(({ data }) => {
        if (cancelled) return;
        setInfo(data);
        setState(data.valid ? "ready" : data.reason === "expired" ? "expired" : "invalid");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    setState("submitting");
    try {
      const { data } = await api.post("/late-order/choice", { token });
      setInfo((prev) => ({ ...prev, ...data }));
      setState("done");
    } catch (err) {
      const status = err.response?.status;
      setState(status === 410 ? "expired" : status === 400 ? "invalid" : "error");
    }
  };

  const order = info.order_number ? `#${info.order_number}` : "";
  const deadline = info.deadline_label || "November 15, 2026, 11:59pm ET";

  let body;
  if (state === "loading") {
    body = <Loader2 size={28} className="animate-spin text-[#8A8A8A]" aria-label="Loading" />;
  } else if (state === "ready" || state === "submitting") {
    const copy = CHOICE_COPY[info.choice];
    body = (
      <Shell testid="late-order-confirm" title={`Order ${order}: keep it, or cancel for a full refund?`}>
        <p data-testid="late-order-picked" className="mt-6 text-[15px] text-[#C9C7C2]">{copy.picked}</p>
        <div className="mt-8 flex justify-center">
          <button type="button" data-testid="late-order-confirm-btn" onClick={confirm} disabled={state === "submitting"} className={primaryBtn}>
            {state === "submitting" && <Loader2 size={14} className="animate-spin" />}
            {copy.button}
          </button>
        </div>
        <p className="mt-8 text-[13px] leading-relaxed text-[#8A8A8A]">
          Changed your mind? Use the other link in our email. Your last answer before {deadline} counts.
        </p>
        <p className="mt-3 text-[13px] text-[#8A8A8A]">Questions? Email <Email />.</p>
      </Shell>
    );
  } else if (state === "done") {
    const thanks = THANKS[info.choice];
    body = (
      <Shell testid={`late-order-done-${info.choice}`} eyebrow={`Order ${order}`} title={thanks.title}>
        <p className="mt-6 text-[15px] leading-relaxed text-[#C9C7C2]">{thanks.body}</p>
        <div className="mt-10">
          <Link to="/" className={secondaryBtn}>Home</Link>
        </div>
      </Shell>
    );
  } else if (state === "expired") {
    body = (
      <Shell testid="late-order-expired" eyebrow={order ? `Order ${order}` : null} title="This link has closed">
        <p className="mt-6 text-[15px] leading-relaxed text-[#C9C7C2]">
          Answers closed on {deadline}. Questions? Email <Email />.
        </p>
      </Shell>
    );
  } else if (state === "invalid") {
    body = (
      <Shell testid="late-order-invalid" title="This link doesn't work">
        <p className="mt-6 text-[15px] leading-relaxed text-[#C9C7C2]">
          Open the full link from our email. Still stuck? Email <Email /> with your order number.
        </p>
      </Shell>
    );
  } else {
    body = (
      <Shell testid="late-order-error" title="Something went wrong">
        <p className="mt-6 text-[15px] leading-relaxed text-[#C9C7C2]">
          Please try again in a minute, or email <Email /> with your order number.
        </p>
      </Shell>
    );
  }

  return (
    <div data-testid="late-order-page" className="ph-no-capture bg-[#0A0A0A] min-h-[70vh] flex items-center justify-center px-6 py-20 sm:py-28">
      <SEO title="Your Order | AERWEAR" description="Keep your AERWEAR order, or cancel for a full refund." noindex />
      {body}
    </div>
  );
}
